import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Loader from '@components/Loader';
import { apiURL, useToken } from '@/helpers/apiURL';
import { getTimezoneOffset } from 'date-fns-tz';
import EventBus from '@/helpers/eventBus';
import { DeliveryHistoryData, DeliveryHistoryStatsData, DeliveryItem } from './types';
import DeliveryHistoryStats from './DeliveryHistoryStats';
import DeliveryHistoryTable from './DeliveryHistoryTable';
import DeliveryHistoryFiltersContainer from './DeliveryHistoryFiltersContainer';

// Negative value in ms
const chicagoTimeOffset = getTimezoneOffset('America/Chicago');

type DeliveryHistoryProps = {
  deliveryId?: string,
  clientId?: string
};

const DeliveryHistoryList: React.FC<DeliveryHistoryProps> = (props) => {
  const { deliveryId, clientId } = props;
  const [deliveryHistory, setDeliveryHistory] = useState<DeliveryHistoryData>({
    Items: [],
    TotalPages: 1
  });
  const [deliveryStats, setDeliveryStats] = useState<DeliveryHistoryStatsData>({
    Items: []
  });
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState((new Date(Date.now() + chicagoTimeOffset)).setUTCHours(0, 0, 0, 0) - chicagoTimeOffset);
  const [to, setTo] = useState((new Date(Date.now() + chicagoTimeOffset)).getTime() - chicagoTimeOffset);
  const [isLoading, setIsLoading] = useState(true);
  const [errorCheckboxSelected, setErrorCheckboxSelected] = useState(false);
  const [fileFilter, setFileFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [leagueFilter, setLeagueFilter] = useState('');
  const [pageSize, setPageSize] = useState(100);
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchInput, setGlobalSearchInput] = useState('');

  // Ref for debounce timer for global search
  const globalSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Parses object returned from API
  const parseDeliveryHistory = (json: any, setter: Function) => {
    const data = {
      Items: json.Items,
      PageSize: json.PageSize,
      TotalPages: json.TotalPages,
    };

    setter(data);
  };

  // Retrieves data from API
  const { privateFetch } = useToken();
  const getHistory = useCallback(async () => {
    const timeoutId = setTimeout(() => setIsLoading(true), 500);
    let statsUrl = `${apiURL}metrics/stats?from=${from}&to=${to}`;
    let metricsUrl = `${apiURL}metrics?from=${from}&to=${to}&page=${page}&errorOnly=${errorCheckboxSelected}&size=${pageSize}`;

    if (clientId) {
      statsUrl += `&client=CLIENT${clientId}`;
      metricsUrl += `&client=CLIENT${clientId}`;
    }
    if (deliveryId) {
      statsUrl += `&delivery=${deliveryId}`;
      metricsUrl += `&delivery=${deliveryId}`;
    }
    if (fileFilter) {
      statsUrl += `&file=${encodeURIComponent(fileFilter)}`;
      metricsUrl += `&file=${encodeURIComponent(fileFilter)}`;
    }
    if (typeFilter) {
      statsUrl += `&type=${typeFilter}`;
      metricsUrl += `&type=${typeFilter}`;
    }
    if (leagueFilter) {
      statsUrl += `&league=${leagueFilter}`;
      metricsUrl += `&league=${leagueFilter}`;
    }
    if (globalSearch) {
      statsUrl += `&search=${encodeURIComponent(globalSearch)}`;
      metricsUrl += `&search=${encodeURIComponent(globalSearch)}`;
    }

    try {
      // Request delivery history stats
      const statsResponse = await privateFetch(statsUrl);
      const statsJson = await statsResponse.json();

      if (!statsResponse.ok) {
        throw new Error(statsJson.error || 'Failed to load Delivery History Stats');
      }

      if (statsJson && !statsJson.error) {
        parseDeliveryHistory(statsJson, setDeliveryStats);
      }

      // Request delivery history metrics
      const metricsResponse = await privateFetch(metricsUrl);
      const metricsJson = await metricsResponse.json();

      if (!metricsResponse.ok) {
        throw new Error(metricsJson.error || 'Failed to load Delivery History Metrics');
      }

      if (metricsJson && !metricsJson.error) {
        parseDeliveryHistory(metricsJson, setDeliveryHistory);
      }
    } catch (error) {
      EventBus.dispatch('notify', { 
        variant: 'error', 
        message: `Error: ${error instanceof Error ? error.message : 'Failed to load Delivery History data'}`
      });

      setDeliveryStats({ Items: [] });
      setDeliveryHistory({
        Items: [],
        TotalPages: 1
      });
    } finally {
      setIsLoading(false);
      clearTimeout(timeoutId);
    }
  }, [clientId, deliveryId, privateFetch, from, to, page, fileFilter, typeFilter, leagueFilter, errorCheckboxSelected, pageSize, globalSearch]);

  // Handles changes made to the to/from dates
  const handleDateChange = useCallback((e: any) => {
    setFrom(e.from);

    if (e.to > (new Date()).getTime() - chicagoTimeOffset) {
      setTo((new Date()).getTime() - chicagoTimeOffset);
    }
    else {
      setTo(e.to);
    }

    setPage(1);
  }, []);

  const handleCheckboxSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorCheckboxSelected(e.currentTarget.checked);
  }, []);

  const handleFilterSubmit = useCallback((filters: any) => {
    setFileFilter(filters.file);
    setTypeFilter(filters.type);
    setLeagueFilter(filters.league);
  }, []);

  const handlePageSizeChange = useCallback((e: number) => {
    setPage(1);
    setPageSize(e);
  }, []);

  const handleGlobalSearchChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalSearchInput(value);
    setPage(1);

    // Clear any existing timer
    if (globalSearchDebounceRef.current) {
      clearTimeout(globalSearchDebounceRef.current);
    }

    // Set a new timer with 1 second delay
    globalSearchDebounceRef.current = setTimeout(() => {
      setGlobalSearch(value);
    }, 1000); // 1000ms (1 second) delay
  }, []);

  const handleClearSearch = useCallback(() => {
    // Clear any existing timer
    if (globalSearchDebounceRef.current) {
      clearTimeout(globalSearchDebounceRef.current);
      globalSearchDebounceRef.current = null;
    }

    setGlobalSearchInput('');
    setGlobalSearch('');
    setPage(1);
  }, []);

  useEffect(() => {
    getHistory();
  }, [getHistory]);

  // date: 1657898570000
  // filename: MLB_EVENTS.XML
  // duration: 923 ms
  // error: Error message


  // Filter items based on global search if it exists
  const filteredItems = useMemo(() => {
    return globalSearch 
      ? deliveryHistory?.Items?.filter((item: DeliveryItem) => {
          // Check if any field in the item contains the search term
          return Object.values(item).some(value => 
            value && String(value).toLowerCase().includes(globalSearch.toLowerCase())
          );
        })
      : deliveryHistory?.Items;
  }, [globalSearch, deliveryHistory?.Items]);

  // Calculate total pages for pagination
  const totalPages = useMemo(() => {
    return globalSearch && filteredItems?.length > 0
      ? Math.ceil(filteredItems.length / pageSize)
      : (deliveryHistory.TotalPages || 1);
  }, [globalSearch, filteredItems?.length, pageSize, deliveryHistory.TotalPages]);

  return (
    <>
      <DeliveryHistoryFiltersContainer
        fromDate={new Date(from)}
        toDate={new Date(to)}
        onDateChange={handleDateChange}
        fileFilter={fileFilter}
        typeFilter={typeFilter}
        leagueFilter={leagueFilter}
        onFilterSubmit={handleFilterSubmit}
        errorOnly={errorCheckboxSelected}
        onErrorCheckboxChange={handleCheckboxSelect}
        globalSearchInput={globalSearchInput}
        onGlobalSearchChange={handleGlobalSearchChange}// This prop is kept for backward compatibility
        onClearSearch={handleClearSearch}
        globalSearch={globalSearch}
        maxDaysInPast={14}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <DeliveryHistoryStats
            statsData={deliveryStats.Items}
            globalSearch={globalSearch}
            filteredItems={filteredItems}
          />

          <DeliveryHistoryTable
            items={filteredItems}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={[100, 500, 1000]}
            setPageSize={handlePageSizeChange}
            globalSearch={globalSearch}
            deliveryId={deliveryId}
            clientId={clientId}
          />
        </>
      )}
    </>
  );
};

export default DeliveryHistoryList;
